import type { FacebookPageListItem } from "@/modules/facebook/presentation/api/types";
import { AppImage } from "@/shared/ui/AppImage";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

type FacebookPageComboboxProps = {
  pages: FacebookPageListItem[];
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  queryKey?: string[];
};

function PageRow({
  page,
  compact = false,
}: {
  page: FacebookPageListItem;
  compact?: boolean;
}) {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <AppImage
        src={page.pagePictureUrl}
        alt=""
        className={`${compact ? "size-7" : "size-8"} shrink-0 rounded-full border bg-muted`}
        showLoading={false}
      />
      <span className="min-w-0 truncate">
        <span className="block truncate text-sm">{page.pageName}</span>
        {!compact && page.pageCategory ? (
          <span className="block truncate text-muted-foreground text-xs">
            {page.pageCategory}
          </span>
        ) : null}
      </span>
    </span>
  );
}

export function FacebookPageCombobox({
  pages,
  value,
  onValueChange,
  disabled,
  placeholder = "ເລືອກເພຈ",
  queryKey = ["facebook-page-combobox"],
}: FacebookPageComboboxProps) {
  // อย่าใช้ผล preload ที่เป็น null ตอนรายชื่อเพจยังโหลดไม่เสร็จ
  const selectedPageAvailable = pages.some((page) => page.id === value);
  return (
    <InfiniteCombobox<FacebookPageListItem>
      queryKey={[
        ...queryKey,
        selectedPageAvailable ? "selected-page-ready" : "selected-page-pending",
      ]}
      value={value}
      onValueChange={onValueChange}
      placeholder={placeholder}
      disabled={disabled}
      queryFn={async ({ search }) => ({
        items: pages.filter((page) =>
          page.pageName.toLowerCase().includes(search.toLowerCase()),
        ),
        nextPage: null,
      })}
      preloadQueryFn={async (id) =>
        pages.find((page) => page.id === id) ?? null
      }
      getLabel={(page) => page.pageName}
      getValue={(page) => page.id}
      renderItem={(page) => <PageRow page={page} />}
      renderSelected={(page) => <PageRow page={page} compact />}
    />
  );
}
